/*
 * Regions: named parts of server-rendered HTML that scripts refresh without reloading the page.
 *
 * The HTML comes from Shopify's Section Rendering API, as in Horizon: a section requested with
 * `section_id` (the product section for a variant, the collection section for filters, the predictive
 * search section for the header search), or the cart's sections requested together (`sections`). In Liquid a region is a wrapper `<div data-region="name">`
 * (a `<span>` inside text) within those sections, drawn with `display: contents` (critical.css) so it
 * never changes the layout. `load()` requests a URL and returns the named regions it holds; `parse()`
 * does the same for HTML already at hand; `apply()` swaps them into every matching region on the page
 * and keeps the focus on an element with the same id; `names()` lists the regions on the page.
 */

window.themeRegions = {
  async load(names, { url = window.location.href, ...init } = {}) {
    const response = await fetch(url, init);
    if (!response.ok) throw new Error(`Regions request failed: ${response.status}`);
    return this.parse(await response.text(), names);
  },

  parse(html, names) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const regions = new Map();
    for (const name of names) {
      const region = doc.querySelector(`[data-region="${CSS.escape(name)}"]`);
      if (region) regions.set(name, region);
    }
    return regions;
  },

  apply(regions) {
    const focusedId = document.activeElement?.id;
    for (const [name, source] of regions) {
      for (const target of document.querySelectorAll(`[data-region="${CSS.escape(name)}"]`)) {
        target.replaceChildren(...source.cloneNode(true).childNodes);
      }
    }
    if (focusedId && document.activeElement === document.body) {
      document.getElementById(focusedId)?.focus({ preventScroll: true });
    }
  },

  names(prefix = '') {
    const names = [...document.querySelectorAll('[data-region]')].map((region) => region.dataset.region);
    return [...new Set(names)].filter((name) => name.startsWith(prefix));
  },
};
